import { TeamSide } from "../constants/teams";
import { LineUpPlayer } from "../types/gameFull";

const TEAM_SIDES: TeamSide[] = ["away", "home"];
import { getBattingOrder } from "./lineupDraft";

/**
 * How a team's edited batting order differs from its lineup: the players new
 * to it (who may need adding to the roster) and whether anything changed.
 */
export type LineupDiff = {
  /** Players new to the batting order, in the order they were added */
  additions: LineUpPlayer[];
  /** Existing players were moved/removed, or additions aren't at the end */
  needsReorder: boolean;
  /** The full edited batting order */
  order: LineUpPlayer[];
};

const sameIds = (a: LineUpPlayer[], b: LineUpPlayer[]) =>
  a.length === b.length && a.every((p, i) => p.playerId === b[i].playerId);

export const diffLineupDraft = (
  lineUp: LineUpPlayer[],
  draftOrder: LineUpPlayer[],
): LineupDiff => {
  const original = getBattingOrder(lineUp);
  const originalIds = new Set(original.map((p) => p.playerId));
  const kept = draftOrder.filter((p) => originalIds.has(p.playerId));
  const additions = draftOrder.filter((p) => !originalIds.has(p.playerId));

  return {
    additions,
    // Appends only: the original order intact, with additions after it
    needsReorder:
      !sameIds(kept, original) || !sameIds(draftOrder, [...kept, ...additions]),
    order: draftOrder,
  };
};

export const hasLineupChanges = (diff: LineupDiff) =>
  diff.additions.length > 0 || diff.needsReorder;

/**
 * Whether saving the draft would write anything: either team's batting order
 * differs from the game's lineup. Same check the save uses.
 */
export const isLineupDraftChanged = (
  lineUp: Record<TeamSide, LineUpPlayer[]>,
  draft: Record<TeamSide, { battingOrder: LineUpPlayer[] }>,
) =>
  TEAM_SIDES.some((side) =>
    hasLineupChanges(diffLineupDraft(lineUp[side], draft[side].battingOrder)),
  );
